import { useCallback, useState } from 'react';
import { Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { getErrorLogCount, getFormattedErrorLogs } from 'Utils/errorLogStore';

/**
 * Copy text to the clipboard, falling back to a hidden textarea +
 * execCommand for older/mobile webviews where navigator.clipboard is
 * unavailable (e.g. some Capacitor contexts).
 */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

type CopyState = 'idle' | 'copied' | 'error';

const FEEDBACK_DURATION_MS = 1600;

/**
 * Dev-only affordance that copies all buffered error-level logs (console.error
 * + Logger.error) to the clipboard as formatted JSON. Rendering is gated by the
 * caller (dev environments only); this component assumes it should be shown.
 */
export function CopyErrorLogsButton() {
  const [state, setState] = useState<CopyState>('idle');

  const handleCopy = useCallback(async () => {
    try {
      await copyToClipboard(getFormattedErrorLogs());
      setState('copied');
    } catch {
      setState('error');
    }
    setTimeout(() => setState('idle'), FEEDBACK_DURATION_MS);
  }, []);

  const count = getErrorLogCount();
  const label =
    state === 'copied'
      ? 'Copied!'
      : state === 'error'
        ? 'Copy failed'
        : `Copy error logs (${count})`;
  const icon =
    state === 'copied' ? (
      <CheckIcon />
    ) : state === 'error' ? (
      <ErrorOutlineIcon />
    ) : (
      <ContentCopyIcon />
    );

  return (
    <Button
      onClick={handleCopy}
      size="small"
      startIcon={icon}
      aria-label="Copy error logs to clipboard"
      sx={{
        textTransform: 'none',
        fontSize: '12px',
        color: '#40E16D',
        borderColor: 'rgba(64, 225, 109, 0.46)',
        backgroundColor: 'rgba(64, 225, 109, 0.1)',
        border: '1px solid rgba(64, 225, 109, 0.46)',
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: 'rgba(64, 225, 109, 0.2)' },
      }}
    >
      {label}
    </Button>
  );
}

export default CopyErrorLogsButton;
